import { getActionHandler, useActionDispatcher, useApi } from "@judo/actions";
import {
	type DialogCloseResult,
	useApplication,
	useItemContainerConfig,
	useColumnCustomizers,
	useDataSelector,
	useDataStore,
	useMuiProOptional,
	useNavigation,
	usePageContextOptional,
	useRefreshSignal,
	useResolvedPageActionOverrides,
	useRuntimeConfigOptional,
	useSelectorSelectionOptional,
	useTableRowHighlighting,
} from "@judo/core";
import { useConfirmation, useDialog, useNotifications } from "@judo/feedback";
import { useModelLabel } from "@judo/i18n";
import { CheckboxSelection, TableRepresentation } from "@judo/model-api";
import type {
	Action,
	ActionDefinition,
	Button as ButtonElement,
	Column,
	PageDefinition,
	RelationType,
	Table,
	TableRowHighlightConfig,
} from "@judo/model-api";
import { getTableTestId } from "@judo/test-ids";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import RemoveIcon from "@mui/icons-material/Remove";
import {
	Box,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Grid,
	IconButton,
	Pagination,
	Typography,
} from "@mui/material";
import {
	DataGrid,
	type GridColDef,
	type GridFilterModel,
	type GridPaginationModel,
	type GridRowClassNameParams,
	type GridRowParams,
	type GridRowSelectionModel,
	type GridSortModel,
	useGridApiRef,
} from "@mui/x-data-grid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTableData } from "../hooks/use-table-data";
import { buildQueryCustomizer } from "../utils/build-query-customizer";
import { buildTableMask } from "../utils/build-page-mask";
import { getVisibleButtons } from "../utils/get-visible-buttons";
import { getColumnType, getEnumerationValueOptions, isColumnSortable } from "../utils/table-column-utils";
import { getDestructiveConfirmationOptions, isDestructiveAction } from "../utils/destructive-action-utils";
import { findActionByDefinition } from "../utils/find-action-by-definition";
import type { BulkRemoveProgressDialogProps } from "../dialogs/BulkRemoveProgressDialog";
import { BulkRemoveProgressDialog } from "../dialogs/BulkRemoveProgressDialog";
import { FixedTableFilters } from "./FixedTableFilters";
import { IconRenderer } from "./IconRenderer";
import { RowActionCell } from "./RowActionCell";
import { TableToolbar } from "./TableToolbar";

export interface TableRendererProps {
	element: Table;
	page?: PageDefinition;
}

/**
 * Renderer for Table elements using MUI X DataGrid.
 */
export function TableRenderer({ element, page: pageProp }: TableRendererProps) {
	// API ref for programmatic DataGrid control (e.g., autosizing columns)
	const apiRef = useGridApiRef();

	// MUI Pro detection — use DataGridPro when available for advanced features
	const muiPro = useMuiProOptional();
	const DataGridComponent = muiPro?.getComponent("DataGrid") ?? DataGrid;
	const isProEnabled = muiPro?.isProEnabled ?? false;

	const { t } = useTranslation();

	// Track row selection for bulk actions
	const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
		type: "include",
		ids: new Set(),
	});

	// Track filter button element for panel positioning
	// This allows the filter panel to anchor to the filter button instead of the default position
	const [filterButtonEl, setFilterButtonEl] = useState<HTMLButtonElement | null>(null);

	// Compute hasSelection from rowSelectionModel
	const hasSelection = useMemo(() => {
		// type "include": ids contains selected rows (size > 0 means has selection)
		// type "exclude": all rows EXCEPT ids are selected (always has selection unless all rows excluded)
		return rowSelectionModel.type === "include" ? rowSelectionModel.ids.size > 0 : true;
	}, [rowSelectionModel]);

	// Get page context to access transfer ID and page definition
	const pageContext = usePageContextOptional();

	// Resolve effective page: prefer prop, fall back to context (for embedded tables)
	const page = pageProp ?? pageContext?.pageDefinition;

	// Get runtime config to check for forceShowTotalCountForLazyTables
	const runtimeConfig = useRuntimeConfigOptional();

	// Detect selector pages (opened via Add/Set selector actions)
	const isSelector = String(page?.isSelector) === "true";
	const selectorOwnerEntity = pageContext?.params?.ownerTransfer as Record<string, unknown> | undefined;

	// Get required contexts for action dispatcher
	const navigation = useNavigation();
	const dataStore = useDataStore();
	const application = useApplication();
	const notifications = useNotifications();
	const { confirm } = useConfirmation();
	const dialog = useDialog();

	// Stable empty array reference to avoid triggering re-renders
	const emptyArray = useMemo(() => [], []);

	// Determine effective showTotalCount value
	// For lazy (non-eager) tables, runtime config can override model setting
	const effectiveShowTotalCount = useMemo(() => {
		const isLazyTable = !element.isEager;
		const forceEnabled = runtimeConfig?.config.features?.forceShowTotalCountForLazyTables ?? false;

		if (isLazyTable && forceEnabled) {
			return true;
		}

		return element.showTotalCount ?? false;
	}, [element.isEager, element.showTotalCount, runtimeConfig]);

	// Check if table has a dataElement (relation) - for eager tables in VIEW pages
	// CRITICAL: Only extract relation property if NOT in a TABLE page container
	// TABLE pages have dataElement but store data as { __items: [...] } at page level
	// VIEW pages with embedded tables need to extract the relation property from parent transfer
	const relationName = useMemo(() => {
		const container = page?.container as any;
		const isTablePage = container?.type === "TABLE";

		if (!isTablePage && element.dataElement && typeof element.dataElement === "object") {
			return element.dataElement.name;
		}
		return null;
	}, [element.dataElement, page?.container]);

	// Compute the table mask for query customizers.
	// For TABLE pages, include pageContainer.additionalMaskAttributes.
	// For embedded tables (in VIEW pages), only table-level mask attributes are included.
	const tableMask = useMemo(() => {
		const container = page?.container as any;
		const isTablePage = container?.type === "TABLE";
		return buildTableMask(element, isTablePage ? container : undefined);
	}, [element, page?.container]);

	// Subscribe to transfer data from DataContext
	// The selector returns the raw data from the store (could be array, object, or undefined)
	// CRITICAL: selector must return stable references to avoid infinite loops
	const entityData = useDataSelector(
		useCallback(
			(state) => {
				const transferId = pageContext?.transferId;
				if (!transferId) {
					return undefined;
				}
				const transferState = state.transfers.get(transferId);

				// If table has a relation (eager table in VIEW page), extract that property
				if (relationName && transferState?.data) {
					return (transferState.data as any)[relationName];
				}

				// Otherwise return the full transfer data (for page-level tables)
				return transferState?.data;
			},
			[pageContext?.transferId, relationName]
		)
	);

	// Subscribe to loading state from DataContext
	const isLoading = useDataSelector(
		useCallback(
			(state) => {
				const transferId = pageContext?.transferId;
				if (!transferId) {
					return false;
				}
				return state.transfers.get(transferId)?.isLoading ?? false;
			},
			[pageContext?.transferId]
		)
	);

	// Normalize the data to always be an array, with stable references
	// Handler may store data as:
	// - Array directly (legacy or client-side data)
	// - { __items: [...], __totalCount?: number } object (from table list handler)
	// - Single transfer object
	const rawData = useMemo(() => {
		if (!entityData) {
			return emptyArray;
		}
		if (Array.isArray(entityData)) {
			return entityData;
		}
		// Check for __items format from table list handler
		if (typeof entityData === "object" && "__items" in entityData && Array.isArray(entityData.__items)) {
			return entityData.__items as unknown[];
		}
		// Single transfer - wrap in array for table display
		return [entityData];
	}, [entityData, emptyArray]);

	// Extract total count from transfer data if available (for server-side pagination)
	const totalCount = useMemo(() => {
		if (typeof entityData === "object" && entityData && "__totalCount" in entityData) {
			return typeof entityData.__totalCount === "number" ? entityData.__totalCount : undefined;
		}
		return undefined;
	}, [entityData]);

	// Get current transfer data from data store (for VIEW pages where transfer is loaded)
	const storedTransfer = useDataSelector(
		useCallback(
			(state) => {
				const transferId = pageContext?.transferId;
				return transferId ? state.transfers.get(transferId)?.data : undefined;
			},
			[pageContext?.transferId]
		)
	);

	// Build transfer for action context
	// For TABLE pages with signedIdentifier (relation tables), create transfer from params
	// since the transfer isn't stored in the data store - only the signedIdentifier is available
	// For VIEW pages, use the stored transfer from data store
	const transfer = useMemo(() => {
		// For relation tables, create transfer from signedIdentifier in params
		// This takes priority because storedTransfer might be the table list data (__items)
		// which is NOT the owner transfer we need for API calls
		const signedIdentifier = pageContext?.params?.signedIdentifier as string | undefined;
		if (signedIdentifier && pageContext?.transferId) {
			return {
				__identifier: pageContext.transferId,
				__signedIdentifier: signedIdentifier,
			};
		}

		// If we have a stored transfer from the data store (VIEW pages), use it
		// But skip if it looks like table data (has __items - that's list data, not an transfer)
		if (storedTransfer && !("__items" in storedTransfer)) {
			return storedTransfer;
		}

		return undefined;
	}, [storedTransfer, pageContext?.transferId, pageContext?.params]);

	// Get API client for REST operations
	const api = useApi();

	// Get rowsPerPage (defaults to 10 if not specified or invalid)
	const rowsPerPage = element.rowsPerPage && element.rowsPerPage > 0 ? element.rowsPerPage : 10;

	// Refs to hold current table state and dispatch for onDialogClose callback
	// These allow the callback to access current state without recreating dispatcher context
	const tableStateRef = useRef<{
		sortModel: GridSortModel;
		filterModel: GridFilterModel;
		paginationModel: GridPaginationModel;
	}>({ sortModel: [], filterModel: { items: [] }, paginationModel: { page: 0, pageSize: rowsPerPage } });

	// Ref to hold dispatch function for use in onDialogClose callback
	// Uses any type for action to allow passing queryCustomizer and countRecords
	const dispatchRef = useRef<((action: any) => Promise<unknown>) | null>(null);

	// Get resolved page action overrides from customizations
	const actionOverrides = useResolvedPageActionOverrides();

	// Resolve this table's own refresh action from its toolbar buttons (by xmi:id).
	// This avoids searching page.actions by @type, which would pick the wrong action
	// when a page has multiple RefreshActionDefinitions (e.g., a view + embedded table).
	const refreshAction = useMemo(() => {
		if (element.isEager || !page) return undefined;
		const btn = element.tableActionButtonGroup?.buttons?.find(
			(b) => b.actionDefinition?.["@type"] === "RefreshActionDefinition"
		);
		return btn ? findActionByDefinition(page, btn.actionDefinition) : undefined;
	}, [element.isEager, element.tableActionButtonGroup, page]);

	// Refresh a lazy table preserving current sort/filter/pagination state.
	// Shared by onDialogClose (after create), dispatchRowAction (after row delete),
	// and toolbar (after bulk delete).
	const refreshLazyTable = useCallback(() => {
		const currentDispatch = dispatchRef.current;
		if (!currentDispatch || !refreshAction) {
			return;
		}

		// Build query customizer from current table state to preserve sort/filter/pagination
		const { sortModel, filterModel, paginationModel } = tableStateRef.current;
		const queryCustomizer = buildQueryCustomizer({
			sortModel,
			paginationModel,
			filterModel,
			columns: element.columns,
			mask: tableMask,
		});

		// Dispatch refresh with preserved query parameters
		currentDispatch({
			...refreshAction,
			queryCustomizer,
			...(effectiveShowTotalCount ? { countRecords: true } : {}),
		}).catch((error) => {
			console.error("Failed to refresh table:", error);
		});
	}, [refreshAction, element.columns, effectiveShowTotalCount, tableMask]);

	// Callback to refresh table after lazy dialog operations complete
	// Uses refs to access current state without dependency on their values
	const onDialogClose = useCallback(
		(result?: DialogCloseResult) => {
			// Refresh for lazy creates (not eager) and selector operations (add/set)
			if (result?.type === "created" && result.isEager) {
				return;
			}

			if (result?.type === "created" || result?.type === "selected" || result?.type === "submitted") {
				refreshLazyTable();
			}
		},
		[refreshLazyTable]
	);

	// Memoize action dispatcher context to prevent recreating on every transfer change
	// The dispatcher itself doesn't need to be recreated when transfer data changes
	// Include rowsPerPage in additionalContext for pagination-related dispatches
	// Pass transfer for actions that need owner transfer (e.g., OpenCreateFormAction for non-access relations)
	// Pass isEager so OpenCreateFormActionHandler knows to handle eager mode creation
	// For eager tables, also pass parentTransferId and relationName for create operations
	// For lazy tables, pass onDialogClose to trigger refresh after successful creates
	const dispatcherContext = useMemo(
		() =>
			({
				navigation,
				data: dataStore,
				registry: application,
				api,
				transfer,
				notifications,
				actionOverrides,
				additionalContext: {
					pageTransferId: pageContext?.transferId,
					rowsPerPage,
					isEager: element.isEager,
					// For eager tables embedded in VIEW pages, pass the parent context
					// so CreateActionHandler knows where to add created transfers
					...(element.isEager &&
						relationName && {
							parentTransferId: pageContext?.transferId,
							relationName,
						}),
					// For lazy tables, provide callback to refresh after successful create
					...(!element.isEager && {
						onDialogClose,
						refreshLazyTable,
					}),
					// For selector pages, pass selector context so RefreshActionHandler
					// can call getRelationRange instead of listRelation
					...(isSelector ? { isSelector: true, ownerTransfer: selectorOwnerEntity } : {}),
				},
			}) as unknown as Parameters<typeof useActionDispatcher>[0],
		[
			navigation,
			dataStore,
			application,
			api,
			transfer,
			notifications,
			pageContext?.transferId,
			rowsPerPage,
			element.isEager,
			relationName,
			onDialogClose,
			refreshLazyTable,
			actionOverrides,
			isSelector,
			selectorOwnerEntity,
		]
	);

	// Initialize action dispatcher for non-eager mode
	// This provides the dispatch function needed for server-side table operations
	// Note: transfer is accessed from the data store at dispatch time, not passed as context
	const { dispatch } = useActionDispatcher(dispatcherContext);

	// Store dispatch in ref for use by onDialogClose callback
	dispatchRef.current = dispatch;

	// Watch the page-level refresh signal from ViewContainerRenderer.
	// When an operation handler calls refreshPage(), the signal increments and
	// all embedded tables re-fetch their data.
	const refreshSignal = useRefreshSignal();
	const prevRefreshSignalRef = useRef(refreshSignal);

	useEffect(() => {
		if (refreshSignal !== prevRefreshSignalRef.current) {
			prevRefreshSignalRef.current = refreshSignal;
			if (!element.isEager) {
				refreshLazyTable();
			}
		}
	}, [refreshSignal, element.isEager, refreshLazyTable]);

	// Helper to dispatch row actions with row transfer context
	// This manually calls action handlers with modified context to avoid React hooks rules violations
	const dispatchRowAction = useCallback(
		async (action: Action, rowEntity: unknown) => {
			const actionType = action.actionDefinition["@type"];
			const handlerHook = getActionHandler(action);

			if (!handlerHook) {
				console.error("No handler found for action type:", actionType);
				throw new Error(`No handler for action type: ${actionType}`);
			}

			// Create context with row transfer
			// ownerTransfer = page-level transfer (the owner entity) needed by remove/bulk-remove handlers
			const rowContext = {
				navigation,
				data: dataStore,
				registry: application,
				api,
				transfer: rowEntity,
				ownerTransfer: transfer,
				notifications,
				...dispatcherContext.additionalContext,
			};

			// Call the hook to get the handler function
			const handlerFn = handlerHook(action);

			// Execute the handler with row context
			await handlerFn(rowContext as unknown as Parameters<typeof handlerFn>[0]);

			// For lazy tables, refresh after row delete to re-fetch server data
			// preserving current sort/filter/pagination state
			if (!element.isEager && (actionType === "RowDeleteActionDefinition" || actionType === "RemoveActionDefinition")) {
				refreshLazyTable();
			}
		},
		[
			navigation,
			dataStore,
			application,
			api,
			transfer,
			notifications,
			dispatcherContext.additionalContext,
			element.isEager,
			refreshLazyTable,
		]
	);

	// Use table data hook for data management (eager vs non-eager)
	const {
		rows,
		rowCount,
		paginationModel,
		sortModel,
		filterModel,
		paginationMode,
		sortingMode,
		filterMode,
		onPaginationModelChange,
		onSortModelChange,
		onFilterModelChange,
		loading,
	} = useTableData({
		element,
		refreshAction,
		rawData,
		dispatch: element.isEager ? null : dispatch,
		loading: isLoading,
		showTotalCount: effectiveShowTotalCount,
		totalCount,
		mask: tableMask,
		isProEnabled,
	});

	// Update table state ref when state changes (for use by onDialogClose callback)
	tableStateRef.current = { sortModel, filterModel, paginationModel };

	// Get visible toolbar buttons (filtered by page actions)
	const visibleToolbarButtons = useMemo(() => {
		const allButtons = element.tableActionButtonGroup?.buttons ?? [];
		return getVisibleButtons(allButtons, page?.actions);
	}, [element.tableActionButtonGroup, page?.actions]);

	// Get always-shown filters for the fixed filter bar above the DataGrid
	const alwaysShownFilters = useMemo(() => element.filters?.filter((f) => f.alwaysShown) ?? [], [element.filters]);

	// Get visible row action buttons (filtered by page actions)
	const visibleRowButtons = useMemo(() => {
		const allButtons = element.rowActionButtonGroup?.buttons ?? [];
		return getVisibleButtons(allButtons, page?.actions);
	}, [element.rowActionButtonGroup, page?.actions]);

	// Compute page size options - include modeled rowsPerPage if not in default list
	const pageSizeOptions = useMemo(() => {
		const defaultOptions = [5, 10, 25, 50];
		// Convert to number (model may return string from XML attribute)
		const modeledPageSize = element.rowsPerPage ?? 10;
		if (!defaultOptions.includes(modeledPageSize)) {
			return [...defaultOptions, modeledPageSize].toSorted((a, b) => a - b);
		}
		return defaultOptions;
	}, [element.rowsPerPage]);

	// Column customizer functions for this page
	const columnCustomizers = useColumnCustomizers();

	// Item container config for non-TABLE representations
	const itemContainerConfig = useItemContainerConfig(element["xmi:id"]);

	// Build columns from visual model
	const columns: GridColDef[] = useMemo(() => {
		const dataColumns: GridColDef[] = element.columns.map((col) => {
			const columnType = getColumnType(col);
			const valueOptions = getEnumerationValueOptions(col);

			// Base column definition
			// Use flex so data columns expand to fill available space,
			// with minWidth from model or default to prevent columns from becoming too narrow
			const colDef: GridColDef = {
				field: col.attributeType?.name ?? col.name,
				headerName: col.label ?? col.name,
				renderHeader: () => <TranslatedColumnHeader column={col} />,
				type: columnType,
				flex: 1,
				minWidth: col.width ? Number.parseInt(col.width, 10) : 150,
				sortable: isColumnSortable(col),
				// Add value options for enumeration columns (singleSelect type)
				...(columnType === "singleSelect" && valueOptions ? { valueOptions } : {}),
				// Add valueGetter for date/dateTime columns to convert ISO strings to Date objects
				// MUI X requires Date objects for date/dateTime column types
				...(columnType === "date" || columnType === "dateTime"
					? {
							valueGetter: (value: unknown) => {
								if (value instanceof Date) {
									return value;
								}
								if (typeof value === "string") {
									return new Date(value);
								}
								return null;
							},
						}
					: {}),
			};

			// Boolean columns: render colored circles matching template pattern
			if (columnType === "boolean") {
				colDef.renderCell = (params) => {
					const value = params.value;
					if (value == null) {
						return <RemoveIcon fontSize="small" color="disabled" data-testid="boolean-null" />;
					}
					return value ? (
						<FiberManualRecordIcon fontSize="small" color="success" data-testid="boolean-true" />
					) : (
						<FiberManualRecordIcon fontSize="small" color="error" data-testid="boolean-false" />
					);
				};
			}

			// Apply column customizer if registered for this column
			const columnSourceId = col["xmi:id"];
			const customizer = columnSourceId ? columnCustomizers?.[columnSourceId] : undefined;
			return customizer
				? (customizer(colDef as unknown as Record<string, unknown>, col) as unknown as GridColDef)
				: colDef;
		});

		// Add action column if row actions exist
		if (visibleRowButtons.length > 0) {
			const actionColumn: GridColDef = {
				field: "__actions__",
				headerName: t("system.common.actions", { defaultValue: "Actions" }),
				resizable: false,
				sortable: false,
				filterable: false,
				disableColumnMenu: true,
				display: "flex",
				// Note: Column pinning requires DataGrid Pro/Premium
				// Action column is positioned at the end by being last in array
				renderCell: (params) => {
					return (
						<RowActionCell
							visibleButtons={visibleRowButtons}
							buttonGroupElement={element.rowActionButtonGroup}
							page={page}
							dispatchRowAction={dispatchRowAction}
							rowEntity={params.row}
							confirm={confirm}
						/>
					);
				},
			};
			return [...dataColumns, actionColumn];
		}

		return dataColumns;
	}, [
		element.columns,
		visibleRowButtons,
		element.rowActionButtonGroup,
		page,
		dispatchRowAction,
		confirm,
		t,
		columnCustomizers,
	]);

	// Resolve view/open action from row action buttons for row click → navigation (table + tag mode)
	const viewAction = useMemo(() => {
		if (!visibleRowButtons || !page) return undefined;
		const viewButton = visibleRowButtons.find((b) => {
			const type = b.actionDefinition?.["@type"];
			return type === "OpenPageActionDefinition" || type === "ViewActionDefinition";
		});
		return viewButton ? findActionByDefinition(page, viewButton.actionDefinition) : undefined;
	}, [visibleRowButtons, page]);

	// Handle row click — navigate to detail view unless the user is selecting text
	const handleRowClick = useCallback(
		(params: GridRowParams) => {
			if (!viewAction || !dispatchRowAction) return;
			// If the user selected text (e.g. for copy), don't navigate
			const selection = window.getSelection();
			if (selection && selection.toString().trim().length > 0) return;
			dispatchRowAction(viewAction, params.row);
		},
		[viewAction, dispatchRowAction]
	);

	// Compute selected row transfers from selection model and current rows
	// Used by toolbar for bulk actions (e.g., bulk delete)
	const selectedRows = useMemo(() => {
		if (rowSelectionModel.type === "include" && rowSelectionModel.ids.size === 0) {
			return [];
		}
		const getRowIdValue = (row: any) => row.__signedIdentifier ?? row.__identifier ?? row.__tempId ?? row.id;
		if (rowSelectionModel.type === "include") {
			return rows.filter((row) => rowSelectionModel.ids.has(getRowIdValue(row)));
		}
		// type "exclude": all rows except excluded IDs
		return rows.filter((row) => !rowSelectionModel.ids.has(getRowIdValue(row)));
	}, [rows, rowSelectionModel]);

	// Publish selected transfers to SelectorSelectionContext for dialog footer buttons
	// This allows the Add/Set button in the dialog footer to access the table selection
	const selectorSelection = useSelectorSelectionOptional();
	useEffect(() => {
		if (selectorSelection) {
			selectorSelection.selectedTransfersRef.current = selectedRows;
		}
	}, [selectorSelection, selectedRows]);

	const tableTestId = getTableTestId(element);
	const _tableId = element.sourceId ?? element["xmi:id"] ?? "unknown";

	// Determine whether to render a heading (icon + label) above the DataGrid.
	// Mirrors the old template's `displayTableHeading(table, container)` helper:
	//   elementHasIconOrLabel(table) && !container.isTable() && !isTag
	// - TABLE page containers already show the title in the page header, so skip.
	// - CARD/TAG representations handle labels themselves in CardContainerView.
	const containerType = (page?.container as { type?: string } | undefined)?.type;
	const hasLabel = !!(element.label && element.label.trim().length > 0);
	const hasIcon = !!(element.icon?.iconName && element.icon.iconName.trim().length > 0);
	const showHeading = (hasLabel || hasIcon) && containerType !== "TABLE";

	// Trigger column autosize after rows change so action column fits its content
	const rowCount_ = rows.length;
	useEffect(() => {
		// Skip autosize when table is empty or in card/tag mode
		if (rowCount_ === 0) {
			return;
		}
		// Use requestAnimationFrame to ensure DOM has rendered the cell content
		const frame = requestAnimationFrame(() => {
			apiRef.current?.autosizeColumns({
				includeHeaders: true,
				includeOutliers: true,
			});
		});
		return () => cancelAnimationFrame(frame);
	}, [apiRef, rowCount_]);

	// Callback for lazy bulk remove with per-item progress dialog
	const handleBulkRemoveWithProgress = useCallback(
		async (items: unknown[], actionDefinition: ActionDefinition) => {
			// Resolve the clicked button's action to get the correct relation
			const action = page ? findActionByDefinition(page, actionDefinition) : undefined;
			const relation = (action?.ownerDataElement ?? element.dataElement) as RelationType | undefined;
			if (!relation || !api || !transfer) return undefined;

			return dialog.open<boolean, BulkRemoveProgressDialogProps>({
				component: BulkRemoveProgressDialog,
				props: {
					items: items as any[],
					api,
					relation,
					ownerTransfer: transfer as any,
					displayField: element.columns?.[0]?.attributeType?.name,
				},
				size: "sm",
				fullWidth: true,
				disableBackdropClick: true,
			});
		},
		[dialog, api, transfer, page, element.dataElement, element.columns]
	);

	// Prepare toolbar slot props if table has action buttons
	const hasToolbarButtons = (element.tableActionButtonGroup?.buttons?.length ?? 0) > 0;
	const toolbarSlotProps = hasToolbarButtons
		? {
				buttons: element.tableActionButtonGroup!.buttons,
				testId: tableTestId,
				page,
				hasSelection,
				isEager: element.isEager,
				onDispatch: dispatch,
				selectedRows,
				setFilterButtonEl,
				// Pass current state for refresh action to include in queryCustomizer
				sortModel,
				filterModel,
				paginationModel,
				columns: element.columns,
				mask: tableMask,
				showTotalCount: effectiveShowTotalCount,
				// For lazy tables, provide callbacks to refresh after bulk operations
				...(!element.isEager && {
					onAfterBulkDelete: refreshLazyTable,
					onBulkRemoveWithProgress: handleBulkRemoveWithProgress,
				}),
				confirm,
			}
		: undefined;

	// When MUI Pro is enabled, pin the actions column to the right
	const hasActionColumn = visibleRowButtons.length > 0;
	const pinnedColumns = isProEnabled && hasActionColumn ? { right: ["__actions__"] } : undefined;

	// When MUI Pro is enabled and headerFilters feature is on, enable inline header filters
	const headerFiltersEnabled = isProEnabled && (runtimeConfig?.config.features?.headerFilters ?? false);

	// Row highlighting configuration for this table
	const highlightConfigs = useTableRowHighlighting(element["xmi:id"]);

	// Build getRowClassName callback: evaluate each highlight condition and return matching class names
	// Also adds 'navigable' class when a view action exists so rows show pointer cursor
	const getRowClassName = useMemo(() => {
		const hasHighlights = highlightConfigs && highlightConfigs.length > 0;
		if (!hasHighlights && !viewAction) return undefined;
		return (params: GridRowClassNameParams) => {
			const classes: string[] = [];
			if (viewAction) {
				classes.push("navigable");
			}
			if (hasHighlights) {
				for (const config of highlightConfigs) {
					if (config.condition({ row: params.row as Record<string, unknown> })) {
						classes.push(`judo-highlight-${config.name}`);
					}
				}
			}
			return classes.join(" ");
		};
	}, [highlightConfigs, viewAction]);

	// Build dynamic sx styles for highlight classes + navigable cursor
	const highlightSx = useMemo(() => {
		const styles: Record<string, Record<string, string>> = {};
		if (viewAction) {
			styles["& .MuiDataGrid-row.navigable"] = { cursor: "pointer" };
		}
		if (highlightConfigs && highlightConfigs.length > 0) {
			for (const config of highlightConfigs) {
				styles[`& .MuiDataGrid-row.judo-highlight-${config.name}`] = {
					backgroundColor: config.backgroundColor,
					...(config.color ? { color: config.color } : {}),
				};
			}
		}
		return styles;
	}, [highlightConfigs, viewAction]);

	// Check if this table uses card or tag representation
	const representationComponent = element.representationComponent ?? TableRepresentation.TABLE;
	const isCardRepresentation = representationComponent === TableRepresentation.CARD;
	const isTagRepresentation = representationComponent === TableRepresentation.TAG;

	// For CARD or TAG representations, render alternative view
	if (isCardRepresentation || isTagRepresentation) {
		return (
			<CardContainerView
				element={element}
				rows={rows}
				rowCount={rowCount ?? 0}
				paginationModel={paginationModel}
				onPaginationModelChange={onPaginationModelChange}
				loading={loading}
				testId={tableTestId}
				isTag={isTagRepresentation}
				itemContainerConfig={itemContainerConfig}
				page={page}
				dispatch={dispatch}
				dispatchRowAction={dispatchRowAction}
				visibleToolbarButtons={visibleToolbarButtons}
				visibleRowButtons={visibleRowButtons}
				confirm={confirm}
				isEager={element.isEager}
				refreshLazyTable={refreshLazyTable}
			/>
		);
	}

	return (
		<Box data-testid={tableTestId} sx={{ width: "100%", minWidth: 0, overflow: "auto" }}>
			{showHeading && <TableHeading element={element} testId={tableTestId} />}
			{alwaysShownFilters.length > 0 && (
				<FixedTableFilters
					filters={alwaysShownFilters}
					filterModel={filterModel}
					onFilterModelChange={onFilterModelChange}
					testId={tableTestId}
					collapsibleFilters={element.collapsibleFilters}
					collapsibleFiltersMaxCols={element.collapsibleFiltersMaxCols}
				/>
			)}
			<DataGridComponent
				apiRef={apiRef}
				columns={columns}
				rows={rows}
				rowCount={rowCount}
				getRowId={(row: any) => row.__signedIdentifier ?? row.__identifier ?? row.__tempId ?? row.id}
				onRowClick={handleRowClick}
				rowSelectionModel={rowSelectionModel}
				onRowSelectionModelChange={setRowSelectionModel}
				checkboxSelection={element.checkboxSelection !== CheckboxSelection.DISABLED}
				// On selector pages, disable selection for already-selected items (marked by backend)
				{...(isSelector ? { isRowSelectable: (params: any) => params.row?.__selected !== true } : {})}
				// Pagination
				paginationModel={paginationModel}
				onPaginationModelChange={onPaginationModelChange}
				paginationMode={paginationMode}
				pageSizeOptions={pageSizeOptions}
				// Sorting
				sortModel={sortModel}
				onSortModelChange={onSortModelChange}
				sortingMode={sortingMode}
				// Filtering
				filterModel={filterModel}
				onFilterModelChange={onFilterModelChange}
				filterMode={filterMode}
				// Debounce filter input for lazy tables to prevent hammering the backend
				// Only applies when filterMode is 'server' (non-eager tables)
				filterDebounceMs={element.isEager ? undefined : 500}
				// Loading state
				loading={loading}
				// Custom toolbar slot
				slots={hasToolbarButtons ? { toolbar: TableToolbar } : undefined}
				slotProps={
					hasToolbarButtons
						? {
								toolbar: toolbarSlotProps,
								// Anchor filter panel to the filter button for better UX
								panel: filterButtonEl ? { target: filterButtonEl } : undefined,
							}
						: undefined
				}
				showToolbar={hasToolbarButtons}
				// Other props
				data-testid={`${tableTestId}::grid`}
				disableRowSelectionOnClick={!!viewAction || element.checkboxSelection === CheckboxSelection.DISABLED}
				sx={{ height: 600, overflow: "hidden", display: "grid", ...highlightSx }}
				{...(getRowClassName ? { getRowClassName } : {})}
				autosizeOptions={{
					expand: true,
					includeHeaders: true,
					includeOutliers: true,
				}}
				{...(pinnedColumns ? { pinnedColumns } : {})}
				{...(headerFiltersEnabled ? { headerFilters: true } : {})}
			/>
			{highlightConfigs && highlightConfigs.length > 0 && (
				<TableHighlightLegend configs={highlightConfigs} testId={tableTestId} />
			)}
		</Box>
	);
}

/** Translated column header component for DataGrid. */
function TranslatedColumnHeader({ column }: { column: Column }) {
	const { label } = useModelLabel(column);
	return <>{label}</>;
}

/**
 * Heading rendered above an embedded table (icon + label), mirroring the
 * old Handlebars `table.hbs` `displayTableHeading` block. Uses useModelLabel
 * so the label is translated via i18n.
 */
function TableHeading({ element, testId }: { element: Table; testId: string }) {
	const { label } = useModelLabel(element);
	const hasLabel = !!(element.label && element.label.trim().length > 0);
	const hasIcon = !!(element.icon?.iconName && element.icon.iconName.trim().length > 0);
	return (
		<Grid
			container
			direction="row"
			alignItems="center"
			justifyContent="flex-start"
			data-testid={`${testId}::heading`}
			sx={{ mb: 1 }}
		>
			{hasIcon && (
				<Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
					<IconRenderer icon={element.icon!} />
				</Box>
			)}
			{hasLabel && (
				<Typography variant="h6" component="h2" data-testid={`${testId}::heading::label`}>
					{label}
				</Typography>
			)}
		</Grid>
	);
}

/** Legend component showing table row highlight rules with color swatches. */
function TableHighlightLegend({ configs, testId }: { configs: TableRowHighlightConfig[]; testId: string }) {
	return (
		<Box
			data-testid={`${testId}::highlight-legend`}
			sx={{
				display: "flex",
				flexWrap: "wrap",
				gap: 2,
				mt: 1,
				px: 1,
			}}
		>
			{configs.map((config) => (
				<Box
					key={config.name}
					data-testid={`${testId}::highlight-legend::${config.name}`}
					sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
				>
					<Box
						sx={{
							width: 16,
							height: 16,
							backgroundColor: config.backgroundColor,
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 0.5,
							flexShrink: 0,
						}}
					/>
					<Box component="span" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
						{config.label}
					</Box>
				</Box>
			))}
		</Box>
	);
}

// ============================================
// CARD / TAG CONTAINER VIEW
// ============================================

interface CardContainerViewProps {
	element: Table;
	rows: unknown[];
	rowCount: number;
	paginationModel: { page: number; pageSize: number };
	onPaginationModelChange: (model: { page: number; pageSize: number }) => void;
	loading: boolean;
	testId: string;
	isTag: boolean;
	itemContainerConfig?: import("@judo/model-api").ItemContainerConfig;
	// Action-related props (primarily for TAG mode)
	page?: PageDefinition;
	dispatch?: (action: any) => Promise<unknown>;
	dispatchRowAction?: (action: Action, rowEntity: unknown) => Promise<void>;
	visibleToolbarButtons?: ButtonElement[];
	visibleRowButtons?: ButtonElement[];
	confirm?: (options: any) => Promise<boolean>;
	isEager?: boolean;
	refreshLazyTable?: () => void;
}

/**
 * Alternative view for tables with CARD or TAG representationComponent.
 * Renders data as a responsive grid of cards or a row of chips.
 *
 * TAG mode: Renders an outlined form-field–like container with MUI Chip tags,
 * per-chip remove (via RemoveActionDefinition), chip click for navigation
 * (via OpenPageActionDefinition), and inline toolbar action buttons
 * (Add, Create, Clear, Refresh).
 *
 * CARD mode: Renders a responsive grid of MUI Card components.
 */
function CardContainerView({
	element,
	rows,
	rowCount,
	paginationModel,
	onPaginationModelChange,
	loading,
	testId,
	isTag,
	itemContainerConfig,
	page,
	dispatch,
	dispatchRowAction,
	visibleToolbarButtons,
	visibleRowButtons,
	confirm,
	isEager,
	refreshLazyTable,
}: CardContainerViewProps) {
	const ItemElement = itemContainerConfig?.ItemElement;
	const ToolbarElement = itemContainerConfig?.ToolbarElement;
	const layout = itemContainerConfig?.layout ?? "vertical";
	const pageCount = Math.max(1, Math.ceil(rowCount / paginationModel.pageSize));
	const { label } = useModelLabel(element);

	const handlePageChange = useCallback(
		(_event: React.ChangeEvent<unknown>, newPage: number) => {
			onPaginationModelChange({ ...paginationModel, page: newPage - 1 });
		},
		[onPaginationModelChange, paginationModel]
	);

	// --- Tag mode action resolution ---

	// Resolve view/open action from row action buttons for chip click → navigation
	const viewAction = useMemo(() => {
		if (!visibleRowButtons || !page) return undefined;
		const viewButton = visibleRowButtons.find((b) => {
			const type = b.actionDefinition?.["@type"];
			return type === "OpenPageActionDefinition" || type === "ViewActionDefinition";
		});
		return viewButton ? findActionByDefinition(page, viewButton.actionDefinition) : undefined;
	}, [visibleRowButtons, page]);

	// Resolve RemoveAction from row action buttons for per-chip delete (tag mode only)
	const removeAction = useMemo(() => {
		if (!isTag || !visibleRowButtons || !page) return undefined;
		const removeButton = visibleRowButtons.find((b) => b.actionDefinition?.["@type"] === "RemoveActionDefinition");
		return removeButton ? findActionByDefinition(page, removeButton.actionDefinition) : undefined;
	}, [isTag, visibleRowButtons, page]);

	// Per-chip remove handler
	const handleTagDelete = useCallback(
		(rowEntity: unknown) => {
			if (removeAction && dispatchRowAction) {
				dispatchRowAction(removeAction, rowEntity);
			}
		},
		[removeAction, dispatchRowAction]
	);

	// Per-chip click handler (navigate to detail)
	const handleTagClick = useCallback(
		(rowEntity: unknown) => {
			if (viewAction && dispatchRowAction) {
				dispatchRowAction(viewAction, rowEntity);
			}
		},
		[viewAction, dispatchRowAction]
	);

	// Filter toolbar buttons for tag view:
	// - Hide filter actions (no inline filtering for tags)
	// - Hide bulk actions (no selection concept in tags)
	// - Hide refresh for eager tables
	const tagToolbarButtons = useMemo(() => {
		if (!isTag) return [];
		return (visibleToolbarButtons ?? []).filter((b) => {
			const type = b.actionDefinition?.["@type"];
			if (type === "FilterActionDefinition" || type === "FilterRelationActionDefinition") return false;
			if (b.actionDefinition?.isBulk) return false;
			if (isEager && type === "RefreshActionDefinition") return false;
			return true;
		});
	}, [isTag, visibleToolbarButtons, isEager]);

	// Toolbar action button handler
	const handleToolbarAction = useCallback(
		async (button: ButtonElement) => {
			const actionType = button.actionDefinition?.["@type"];

			// Refresh on lazy tables: reuse existing refresh mechanism
			if (actionType === "RefreshActionDefinition" && !isEager && refreshLazyTable) {
				refreshLazyTable();
				return;
			}

			// Confirm destructive actions (Clear, etc.)
			if (confirm && isDestructiveAction(actionType)) {
				const confirmed = await confirm(getDestructiveConfirmationOptions(actionType!));
				if (!confirmed) return;
			}

			const action = page ? findActionByDefinition(page, button.actionDefinition) : undefined;
			if (action && dispatch) {
				const dispatchPromise = dispatch(action);

				// For clear actions on lazy tables, refresh after the clear completes
				if (actionType === "ClearActionDefinition" && !isEager && refreshLazyTable) {
					dispatchPromise
						.then(() => {
							refreshLazyTable();
						})
						.catch((error: unknown) => {
							console.error("Tag toolbar action failed:", error);
						});
				} else {
					dispatchPromise.catch((error: unknown) => {
						console.error("Tag toolbar action failed:", error);
					});
				}
			}
		},
		[page, dispatch, confirm, isEager, refreshLazyTable]
	);

	// --- TAG mode rendering ---
	if (isTag) {
		return (
			<Box data-testid={testId}>
				{ToolbarElement && <ToolbarElement element={element} totalCount={rowCount} />}
				<Box
					data-testid={`${testId}::tags`}
					sx={{
						position: "relative",
						border: 1,
						borderColor: "action.disabled",
						borderRadius: 1,
						display: "flex",
						flexWrap: "wrap",
						gap: 0.5,
						alignItems: "center",
						minHeight: 56,
						p: 1,
						pt: label ? 2 : 1,
						"&:hover": {
							borderColor: "text.primary",
						},
					}}
				>
					{/* Floating label mimicking outlined text field */}
					{label && (
						<Typography
							component="span"
							data-testid={`${testId}::label`}
							sx={{
								position: "absolute",
								top: 0,
								left: 8,
								transform: "translateY(-50%)",
								px: 0.5,
								backgroundColor: "background.paper",
								color: "text.secondary",
								fontSize: "0.75rem",
								lineHeight: 1,
								pointerEvents: "none",
							}}
						>
							{label}
						</Typography>
					)}

					{/* Content: loading, empty, or chips */}
					{loading ? (
						<CircularProgress size="1.5rem" data-testid={`${testId}::loading`} />
					) : rows.length === 0 ? (
						<Typography variant="body2" color="text.secondary" data-testid={`${testId}::empty`}>
							No items
						</Typography>
					) : (
						rows.map((row, idx) => {
							const rowData = row as Record<string, unknown>;
							const key = getRowKey(rowData, idx);
							if (ItemElement) {
								return (
									<ItemElement
										key={key}
										row={rowData}
										columns={element.columns}
										onDelete={removeAction ? () => handleTagDelete(rowData) : undefined}
										onRowClick={viewAction ? () => handleTagClick(rowData) : undefined}
									/>
								);
							}
							return (
								<DefaultTag
									key={key}
									row={rowData}
									columns={element.columns}
									testId={`${testId}::tag::${idx}`}
									onDelete={removeAction ? () => handleTagDelete(rowData) : undefined}
									onClick={viewAction ? () => handleTagClick(rowData) : undefined}
								/>
							);
						})
					)}

					{/* Spacer to push toolbar buttons to the right */}
					<Box sx={{ flexGrow: 1 }} />

					{/* Inline action buttons for tag container */}
					{tagToolbarButtons.map((button) => (
						<TagToolbarButton key={button["xmi:id"]} button={button} onClick={() => handleToolbarAction(button)} />
					))}
				</Box>

				{pageCount > 1 && (
					<Box data-testid={`${testId}::pagination`} sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
						<Pagination count={pageCount} page={paginationModel.page + 1} onChange={handlePageChange} />
					</Box>
				)}
			</Box>
		);
	}

	// --- CARD mode rendering (unchanged) ---
	return (
		<Box data-testid={testId}>
			{ToolbarElement && <ToolbarElement element={element} totalCount={rowCount} />}
			{loading ? (
				<Box data-testid={`${testId}::loading`} sx={{ display: "flex", justifyContent: "center", p: 4 }}>
					<Typography color="text.secondary">Loading…</Typography>
				</Box>
			) : rows.length === 0 ? (
				<Box data-testid={`${testId}::empty`} sx={{ display: "flex", justifyContent: "center", p: 4 }}>
					<Typography color="text.secondary">No items</Typography>
				</Box>
			) : (
				<Grid
					container
					spacing={2}
					data-testid={`${testId}::cards`}
					direction={layout === "horizontal" ? "row" : "row"}
					sx={{ p: 1 }}
				>
					{rows.map((row, idx) => {
						const rowData = row as Record<string, unknown>;
						if (ItemElement) {
							return (
								<Grid key={getRowKey(rowData, idx)} size={{ xs: 12, sm: 6, md: 4 }}>
									<ItemElement row={rowData} columns={element.columns} />
								</Grid>
							);
						}
						return (
							<Grid key={getRowKey(rowData, idx)} size={{ xs: 12, sm: 6, md: 4 }}>
								<DefaultCard row={rowData} columns={element.columns} testId={`${testId}::card::${idx}`} />
							</Grid>
						);
					})}
				</Grid>
			)}
			{pageCount > 1 && (
				<Box data-testid={`${testId}::pagination`} sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
					<Pagination count={pageCount} page={paginationModel.page + 1} onChange={handlePageChange} />
				</Box>
			)}
		</Box>
	);
}

/** Extract a stable row key from transfer data */
function getRowKey(row: Record<string, unknown>, fallbackIdx: number): string {
	return (row.__signedIdentifier ?? row.__identifier ?? row.__tempId ?? row.id ?? fallbackIdx) as string;
}

/** Default card component: MUI Card showing column label/value pairs. */
function DefaultCard({ row, columns, testId }: { row: Record<string, unknown>; columns: Column[]; testId: string }) {
	return (
		<Card data-testid={testId} variant="outlined">
			<CardContent>
				{columns.map((col) => {
					const field = col.attributeType?.name ?? col.name;
					const value = row[field];
					return (
						<Box key={col["xmi:id"] ?? col.name} sx={{ mb: 0.5 }}>
							<Typography variant="caption" color="text.secondary">
								{col.label ?? col.name}
							</Typography>
							<Typography variant="body2">{value == null ? "—" : String(value)}</Typography>
						</Box>
					);
				})}
			</CardContent>
		</Card>
	);
}

/**
 * Default tag component: MUI Chip showing first column value as label.
 * Supports optional onDelete (per-chip remove) and onClick (navigate to detail).
 */
function DefaultTag({
	row,
	columns,
	testId,
	onDelete,
	onClick,
}: {
	row: Record<string, unknown>;
	columns: Column[];
	testId: string;
	onDelete?: () => void;
	onClick?: () => void;
}) {
	const firstCol = columns[0];
	const field = firstCol?.attributeType?.name ?? firstCol?.name;
	const label = field ? String(row[field] ?? "—") : "—";

	return (
		<Chip
			data-testid={testId}
			label={label}
			variant="outlined"
			onDelete={onDelete}
			onClick={onClick}
			clickable={!!onClick}
		/>
	);
}

/** Inline icon button for tag container toolbar actions. */
function TagToolbarButton({ button, onClick }: { button: ButtonElement; onClick: () => void }) {
	const { label } = useModelLabel(button);
	return (
		<IconButton
			size="small"
			onClick={onClick}
			title={label}
			disabled={String(button.disabled) === "true"}
			data-testid={`tag-action::${button["xmi:id"]}`}
		>
			{button.icon ? (
				<IconRenderer icon={button.icon} />
			) : (
				<Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
					{label}
				</Typography>
			)}
		</IconButton>
	);
}
